/**
 * TLS settings of a database connector (PostgreSQL / MySQL).
 *
 * The mode lives in `config.ssl` and the CA bundle in the credentials
 * (`ssl_ca_cert`, encrypted at rest). The API never returns the bundle: it
 * returns `ssl_ca_cert_info` (subject, expiry, fingerprint) so the form can
 * say what is loaded. Validation here only catches obvious mistakes early;
 * the backend parses the certificate and has the final word.
 */

export type DbSslMode = '' | 'disable' | 'require' | 'verify-ca' | 'verify-full';

export const DB_SSL_MODES: DbSslMode[] = ['', 'disable', 'require', 'verify-ca', 'verify-full'];

export interface CaCertInfo {
  subject?: string;
  issuer?: string;
  not_after?: string;
  fingerprint_sha256?: string;
  count?: number;
  error?: string;
}

const ALIASES: Record<string, DbSslMode> = {
  false: 'disable', no: 'disable', '0': 'disable', off: 'disable', disable: 'disable',
  true: 'require', yes: 'require', '1': 'require', on: 'require', require: 'require',
  prefer: 'require', allow: 'require',
  'verify-ca': 'verify-ca', verify_ca: 'verify-ca',
  'verify-full': 'verify-full', verify_full: 'verify-full',
  'verify-identity': 'verify-full', verify_identity: 'verify-full',
};

/** Canonical mode from whatever the stored config holds ('' = unset). */
export function normalizeSslMode(raw: unknown): DbSslMode {
  if (raw === undefined || raw === null || raw === '') return '';
  if (typeof raw === 'boolean') return raw ? 'require' : 'disable';
  return ALIASES[String(raw).trim().toLowerCase()] ?? '';
}

/** Whether the TLS section applies: PostgreSQL and MySQL, not SQL Server. */
export function supportsDbTls(connectorType: string, baseUrl: string): boolean {
  if (connectorType === 'POSTGRESQL' || connectorType === 'MYSQL') return true;
  if (connectorType !== 'RELATIONAL_DB') return false;
  return !/^\s*(sqlserver|mssql):/i.test(baseUrl);
}

export type PemProblem = 'invalidPem' | 'privateKey' | null;

/** Cheap client-side check of a pasted bundle. */
export function pemProblem(pem: string): PemProblem {
  const text = pem.trim();
  if (!text) return null;
  if (/PRIVATE KEY/.test(text)) return 'privateKey';
  if (!/-----BEGIN CERTIFICATE-----[\s\S]+?-----END CERTIFICATE-----/.test(text)) return 'invalidPem';
  return null;
}

/** Mode the backend will use: a CA with no mode set means verify-full. */
export function effectiveSslMode(mode: DbSslMode, hasCa: boolean): DbSslMode {
  if (mode === '' && hasCa) return 'verify-full';
  return mode;
}
